import React, { useCallback, useState } from "react";
import style from "../../../assets/scss/common/modal/addCheckupInfoModal.module.scss";
import CloseBtn from "../../../assets/images/modal/close_btn.svg";
import { useModal, useAppDispatch, useTimeConverter } from "../../../hooks/hooks";
import { getOrganTestResultAsync, addOrganTestResultAsync } from "../../../store/profile/profileSlice";
import {useTranslation} from "react-i18next";

interface AddCheckupInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileIdx: number;
}

const AddCheckupInfoModal: React.FC<AddCheckupInfoModalProps> = ({ isOpen, onClose, profileIdx }) => {
  const { t } = useTranslation("checkupInfo");
  const [name, setName] = useState("");
  const [loginCode, setLoginCode] = useState("");
  const [nameError, setNameError] = useState("");
  const [loginCodeError, setLoginCodeError] = useState("");
  const [organName, setOrganName] = useState("");
  const [testDate, setTestDate] = useState("");
  const [showData, setShowData] = useState(false);
  const [buttonText, setButtonText] = useState(t('modal.btn.getOrganTest'));
  const [showErrorInfo, setShowErrorInfo] = useState(false);
  const [showAddErrorInfo, setShowAddErrorInfo] = useState(false);
  const { openConfirmModal, openAlertModal } = useModal();
  const { timeConverter } = useTimeConverter();

  const dispatch = useAppDispatch();

  const getOrganTestData = useCallback(
      async (name: string, code: string) => {
        try {
          setShowErrorInfo(false);
          const response = await dispatch(getOrganTestResultAsync({ name, code })).unwrap();
          console.log("get organ test data>", response);
          return response;
        } catch (error) {
          // 소속 검사 결과가 없습니다.
          setShowErrorInfo(true);
          console.log(error);
        }
      },
      [dispatch]
  );

  const addOrganTestData = useCallback(
      async (name: string, code: string) => {
        try {
          setShowErrorInfo(false);
          await dispatch(addOrganTestResultAsync({ profileIdx, name, code })).unwrap();

          openAlertModal({
            title: t('modal.info.title'),
            desc: t('modal.info.complete'),
            callback: () => handleClose(),
          });

          return;
        } catch (error: any) {
          try {
            // JSON 파싱
            const parsedError = JSON.parse(error.message);

            // httpCode가 493인 경우 에러 메시지 표시
            if (parsedError?.errorCode?.httpCode === 493) {
              setShowAddErrorInfo(true); // 에러 메시지 표시
            }
          } catch (parseError) {
            console.error("Error parsing error message:", parseError);
          }
          console.log(error);
        }
      },
      // eslint-disable-next-line
      [dispatch, profileIdx]
  );

  const validateName = (value: string) => {
    if (!value) {
      return t('modal.name.unwritten');
    } else if (!/^[가-힣a-zA-Z0-9]+$/.test(value)) {
      return  t('modal.name.typeValidate');
    }
    return "";
  };

  const validateLoginCode = (value: string) => {
    if (!value) {
      return t('modal.loginCode.typeValidate');
    } else if (!/^[a-zA-Z0-9]{6}$/.test(value)) {
      return t('modal.loginCode.typeValidate');
    }
    return "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(validateName(value));
  };

  const handleLoginCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLoginCode(value);
    setLoginCodeError(validateLoginCode(value));
  };

  const handleCheckupFetch = async () => {
    // 입력 값 검증
    const nameError = validateName(name);
    const loginCodeError = validateLoginCode(loginCode);

    setNameError(nameError);
    setLoginCodeError(loginCodeError);

    if (nameError || loginCodeError) {
      return; // 유효하지 않으면 더 이상의 처리 중지
    }

    if (buttonText === t('modal.btn.add')) {
      openConfirmModal(
          {
            title: t('modal.info.title'),
            desc: `${t('modal.info.desc1')}\n${t('modal.info.desc2')}`,
          },
          async () => {
            try {
              await addOrganTestData(name, loginCode);
            } catch (error) {
              console.log("소속 검사 결과 추가 중 에러 발생:", error);
              setShowAddErrorInfo(true); // 에러 메시지 표시
            }
          }
      );
    } else {
      try {
        // getOrganTestData 함수 호출
        const response = await getOrganTestData(name, loginCode);

        if (response) {
          // 성공적으로 데이터를 받아온 경우 organName과 testDate 설정
          setOrganName(response.organName);
          setTestDate(timeConverter.convertToFormattedDate(response.testEndedAt.split("T")[0]));
          setShowData(true);
          setButtonText(t('modal.btn.add'));
          setShowErrorInfo(false); // 데이터가 성공적으로 받아지면 에러 표시 해제
        }
      } catch (error: any) {
        console.log(error);

        // 에러 처리: 존재하지 않는 데이터일 경우 404 메시지를 확인
        const errorMessage = JSON.parse(error.message);
        if (errorMessage.errorCode && errorMessage.errorCode.httpCode === 404) {
          setShowErrorInfo(true); // 에러 상태 업데이트
        } else {
          // 다른 에러 처리 로직 (필요에 따라 추가)
          console.log("다른 에러 발생:", error);
        }
      }
    }
  };

  const handleClose = () => {
    setName("");
    setLoginCode("");
    setNameError("");
    setLoginCodeError("");
    setOrganName("");
    setTestDate("");
    setShowData(false);
    setShowErrorInfo(false);
    setShowAddErrorInfo(false);
    setButtonText(t('modal.btn.getOrganTest'));
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
      <div className={style.addChildModalOverlay}>
        <div className={style.addChildInfoModal}>
          <div className={style.closeBtnContainer}>
            <img src={CloseBtn} alt="closeBtn" className={style.closeBtn} onClick={handleClose} />
          </div>
          <div className={style.innerModalContainer}>
            <h3 className={style.addChildInfoTitle}>{t('modal.title')}</h3>
            <div className={style.addChildInfoDesc}>
              <span className={style.boldDesc}>{t('modal.desc')}</span>
              <br />
              {t('modal.subDesc')}
            </div>
            <div className={style.innerTextContainer}>
              <div className={style.infoContainer}>
              <span className={style.infoText}>
                <span className={style.requireText}>{t('modal.required')}</span> {t('modal.name.label')}
              </span>
                <input
                    name="name"
                    value={name}
                    onChange={handleNameChange}
                    placeholder={t('modal.name.unwritten')}
                    className={style.inputField}
                />
                {nameError && <div className={style.errorText}>{nameError}</div>}
              </div>
              <div className={style.infoContainer}>
              <span className={style.infoText}>
                <span className={style.requireText}>{t('modal.required')}</span> {t('modal.loginCode.label')}
              </span>
                <input
                    name="loginCode"
                    value={loginCode}
                    onChange={handleLoginCodeChange}
                    placeholder={t('modal.loginCode.unwritten')}
                    className={style.inputField}
                />
                {loginCodeError && <div className={style.errorText}>{loginCodeError}</div>}
              </div>
            </div>

            {showData && (
                <div className={style.innerTextContainer}>
                  <div className={style.infoContainer}>
                    <span className={style.infoText}>{t('modal.organName')}</span>
                    <input
                        name="organName"
                        value={organName}
                        readOnly
                        className={`${style.inputField} ${style.aboutOrganText}`}
                    />
                  </div>
                  <div className={style.infoContainer}>
                    <span className={style.infoText}>{t('modal.completedAt')}</span>
                    <input
                        name="testDate"
                        value={testDate}
                        readOnly
                        className={`${style.inputField} ${style.aboutOrganText}`}
                    />
                  </div>
                </div>
            )}

            {showErrorInfo && (
                <div className={`${style.innerTextContainer} ${style.noTestInfo}`}>
                  {t('modal.info.emptyResult')}
                  <br />
                  {t('modal.info.check')}
                </div>
            )}

            {showAddErrorInfo && (
                <div className={`${style.innerTextContainer} ${style.noTestInfo} ${style.aboutOrganText}`}>
                  {t('modal.info.alreadyAdded')}
                </div>
            )}

            <button className={style.submitBtn} onClick={handleCheckupFetch}>
              {buttonText}
            </button>
          </div>
        </div>
      </div>
  );
};

export default AddCheckupInfoModal;
